import React from "react";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import Container from "ui/Container/Container";
import Grid from "ui/Grid/Grid";
import YandexWidgetReviews from "common/YandexWidgetReviews/YandexWidgetReviews";
import Logo from "assets/icons/logo_new.svg";
import { useBreakpoint } from "helpers/hooks";
import { withTrailSlash } from "helpers/url";
import { cn } from "helpers/style";
import {
  ROUTE_ZC_PLUS_TERMS,
  ROUTE_INSTRUCTION,
  ROUTE_ABOUT,
  ROUTE_MARKETING_POLICY,
  ROUTE_TERMS,
} from "constants/routes";
import Contacts from "./components/Contacts/Contacts";
import Apps from "./components/Apps/Apps";
import Copyright from "./components/Copyright/Copyright";
import Address from "./components/Address/Address";
import Payments from "./components/Payments/Payments";
import Accordion from "./components/Accordion/Accordion";
import NumberOne from "./components/NumberOne/NumberOne";
import styles from "./Footer.module.scss";

const { publicRuntimeConfig } = getConfig();

const Socials = dynamic(() => import("./components/Socials/Socials"));

const ACCORDION_DATA = [
  {
    title: "О компании",
    elements: [
      {
        href: ROUTE_ABOUT,
        title: "О проекте",
        local: true,
      },
      {
        href: "/team/",
        title: "Наша команда",
        local: true,
      },
      {
        href: ROUTE_TERMS,
        title: "Политика конфиденциальности",
      },
      {
        href: "/oferta/",
        title: "Договор оферты",
      },
      {
        href: "/termsuser/",
        title: "Пользовательское соглашение",
      },
      {
        href: "/site_rules/",
        title: "Правила пользования сайтом",
      },
      {
        href: "/working-rules/",
        title: "Правила работы с аптеками",
      },
      {
        href: "/redakcionnaja-politika/",
        title: "Редакционная политика",
      },
      {
        href: ROUTE_MARKETING_POLICY,
        local: true,
        title: "Маркетинговая политика",
      },
      {
        href: "/information/license-dostavka/",
        local: true,
        title: "Лицензия",
      },
    ],
  },
  {
    title: "Разделы",
    elements: [
      {
        href: publicRuntimeConfig.BLOG_URL,
        title: "Блог",
      },
      {
        href: "/partners/",
        title: "Наши партнёры",
      },
      {
        href: "/zdravcityplus/",
        title: "Здравсити Плюс",
        local: true,
      },
      {
        regionDependent: true,
        href: "/pharmacies/",
        title: "Найти аптеку",
        local: true,
      },
      {
        regionDependent: true,
        local: true,
        href: "/drugs/",
        title: "Найти товар",
      },
      {
        local: true,
        href: ROUTE_ZC_PLUS_TERMS,
        title: "Правила Здравсити Плюс",
      },
    ],
  },
  {
    title: "Помощь",
    elements: [
      {
        href: "/information/",
        title: "Контакты",
        local: true,
      },
      {
        href: "/faq/",
        title: "Помощь",
      },
      {
        href: "/vacancies/",
        title: "Вакансии",
      },
      {
        href: withTrailSlash(ROUTE_INSTRUCTION),
        title: "Как сделать заказ",
        local: true,
      },
      {
        href: "/order-get/",
        title: "Как получить заказ",
      },
      {
        regionDependent: true,
        href: "/dostavka/",
        title: "Доставка",
      },
      {
        href: "/dostavka/",
        title: "Оплата",
        hash: "#oplata",
      },
      {
        href: "/garantii/",
        title: "Гарантии",
        local: true,
      },
      { href: "/dostavka/", title: "Возврат и обмен", hash: "#vozvrat" },
      {
        regionDependent: true,
        href: "/zakazat-lekarstva-po-telefonu/",
        title: "Заказать по телефону",
        local: true,
      },
    ],
  },
];
type Props = {
  contactPhone?: string;
  classNameSection?: string;
};

const Footer = ({ contactPhone, classNameSection }: Props) => {
  const breakpoint = useBreakpoint(true);
  const gtTablet = ["laptop", "desktop"].includes(breakpoint);
  const ltLaptop = ["mobile", "tablet"].includes(breakpoint);

  return (
    <footer className={styles.footer}>
      <div className={styles.footerAppBackground}>
        <Container>
          <Grid container flat spacing={20}>
            <Apps />
          </Grid>
        </Container>
      </div>

      <div className={cn([styles.footerSection, classNameSection])}>
        <Container>
          {ltLaptop && (
            <div className={styles.footerOnMobile}>
              <div className={styles.footerLogoContainer}>
                <div>
                  <Logo />
                </div>
                <Contacts mobileVersion contactPhone={contactPhone} />
              </div>
            </div>
          )}

          <Grid container flat spacing={20}>
            {ACCORDION_DATA.map((data, idx) => (
              <Grid key={idx} item sm={12} lg={3}>
                <Accordion
                  showWhatAboutOrder={idx === 1}
                  title={data.title}
                  elements={data.elements}
                />
              </Grid>
            ))}
            <Grid item sm={12} lg={3}>
              {gtTablet && (
                <div className={styles.footerOnLaptop}>
                  <div className={styles.footerLogoContainer}>
                    <Logo />
                  </div>
                  <Address />
                </div>
              )}

              <Socials />

              {ltLaptop && (
                <div className={styles.footerOnMobile}>
                  <Address />
                  <Copyright />
                  <Payments />
                </div>
              )}

              <NumberOne />
              <YandexWidgetReviews />
            </Grid>
          </Grid>
        </Container>
      </div>
      {gtTablet && (
        <div className={styles.footerBottomBackground}>
          <Container>
            <Grid container flat spacing={20}>
              <Grid item sm={12} lg={9}>
                <Contacts contactPhone={contactPhone} />
                <Copyright />
              </Grid>
              <Grid item sm={12} lg={3}>
                <Payments />
              </Grid>
            </Grid>
          </Container>
        </div>
      )}
    </footer>
  );
};

export default Footer;
